import { useEffect, useState } from "react";
// doc-design-jmr: standard card-list design
import { PageFrame, DocCard, DocMenu, Figure, useDocFlow } from "@/components/procurement/DocKit";
import { GitBranch, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";
import { Plus, Loader2, CheckCircle2, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

import { fmtDate } from "@/lib/format";
import { confirmAction } from "@/lib/dialogs";
const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];

export default function ContractorJMRs() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [jmrs, setJmrs] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [jmrRes, woRes] = await Promise.all([api.get("/procurement/contractor-jmrs"), api.get("/procurement/work-orders")]);
      setJmrs(jmrRes.data);
      setWorkOrders(woRes.data.filter((w) => w.status === "APPROVED"));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const lineLabel = (woItemId) => {
    for (const wo of workOrders) {
      const item = wo.items.find((i) => i.id === woItemId);
      if (item) return `${wo.number} — ${item.description}`;
    }
    return woItemId;
  };

  const certify = async (jmr) => {
    setBusyId(jmr.id);
    try {
      await api.post(`/procurement/contractor-jmrs/${jmr.id}/certify`);
      toast.success("JMR certified");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (jmr) => {
    if (!await confirmAction(`Delete draft JMR ${jmr.number}? This can't be undone.`)) return;
    setBusyId(jmr.id);
    try {
      await api.delete(`/procurement/contractor-jmrs/${jmr.id}`);
      toast.success(`JMR ${jmr.number} deleted`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const { openFlow, flowNode } = useDocFlow(load);
  const [q, setQ] = useState("");
  const visible = pf.apply(jmrs, (r) => r.project_id);
  const t = q.trim().toLowerCase();
  const rows = t ? visible.filter((j) => `${j.number} ${lineLabel(j.work_order_item_id)}`.toLowerCase().includes(t)) : visible;

  return (
    <PageFrame testId="contractor-jmrs-page" crumbs={[{ label: "Procurement" }, { label: "Contractor JMRs" }]} title="Contractor JMRs"
      description="Joint measurement of work done against a work order line, capped at 10% over the ordered qty."
      actions={<><ProjectFilter filter={pf} />{canWrite && <CreateJMRDialog workOrders={workOrders} onCreated={load} />}</>}>
      <div className="relative w-full max-w-sm">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number or work order line…" className="h-10 rounded-lg pl-9" data-testid="jmr-search" />
      </div>
      {!loading && rows.length === 0 ? (
        <EmptyState title={jmrs.length ? "No JMRs match" : "No JMRs yet"} testId="jmrs-empty-state" />
      ) : (
        <div className="space-y-3">
          {rows.map((jmr) => (
            <DocCard key={jmr.id} flat testId={`jmr-row-${jmr.number}`} number={jmr.number} date={fmtDate(jmr.measurement_date)}
              line={<span className="font-medium">{lineLabel(jmr.work_order_item_id)}</span>}
              note={jmr.remarks || null}
              right={<>
                <Figure label="Measured qty" value={jmr.measured_qty} strong />
                <StatusPill value={jmr.status} />
                {canWrite && jmr.status === "DRAFT" && <Button className="h-10 rounded-lg" disabled={busyId === jmr.id || !(Number(jmr.measured_qty) > 0)} title={Number(jmr.measured_qty) > 0 ? "" : "Enter the measured qty first"} onClick={() => certify(jmr)} data-testid={`certify-jmr-button-${jmr.number}`}><CheckCircle2 className="h-3.5 w-3.5" /> Certify</Button>}
                {canWrite && jmr.status === "DRAFT" && <EditJMRDialog jmr={jmr} onSaved={load} />}
                <DocMenu testId={`jmr-menu-${jmr.number}`} items={[{ key: "flow", label: "Document flow", icon: GitBranch, onSelect: () => openFlow("CONTRACTOR_JMR", jmr.id) },
                  canWrite && jmr.status === "DRAFT" && { key: "delete", label: "Delete JMR", icon: Trash2, danger: true, onSelect: () => remove(jmr), testId: `delete-jmr-${jmr.number}` }]} />
              </>} />
          ))}
        </div>
      )}
      {flowNode}
    </PageFrame>
  );
}

function CreateJMRDialog({ workOrders, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ work_order_item_id: "", measurement_date: "", measured_qty: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/contractor-jmrs", form);
      toast.success("JMR recorded");
      setForm({ work_order_item_id: "", measurement_date: "", measured_qty: "", remarks: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-jmr-button"><Plus className="h-4 w-4" /> New JMR</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Contractor JMR</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-jmr-form">
          <div>
            <Label>Work order line</Label>
            <Select required value={form.work_order_item_id} onValueChange={(v) => setForm({ ...form, work_order_item_id: v })}>
              <SelectTrigger data-testid="jmr-wo-item-select" className="mt-1.5"><SelectValue placeholder="Select an approved WO line" /></SelectTrigger>
              <SelectContent>
                {workOrders.flatMap((wo) => wo.items.map((item) => (
                  <SelectItem key={item.id} value={item.id}>{wo.number} — {item.description}</SelectItem>
                )))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" data-testid="jmr-date-input" value={form.measurement_date} onChange={set("measurement_date")} className="mt-1.5" /></div>
            <div><Label>Measured qty</Label><Input required type="number" step="0.001" data-testid="jmr-qty-input" value={form.measured_qty} onChange={set("measured_qty")} className="mt-1.5" /></div>
          </div>
          <div><Label>Remarks</Label><Input data-testid="jmr-remarks-input" value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-jmr-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function EditJMRDialog({ jmr, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ measurement_date: "", measured_qty: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ measurement_date: jmr.measurement_date, measured_qty: Number(jmr.measured_qty) > 0 ? String(jmr.measured_qty) : "", remarks: jmr.remarks || "" });
  }, [open, jmr]);

  const save = async (e) => {
    e.preventDefault();
    if (!(Number(form.measured_qty) > 0)) {
      toast.error("Enter the measured quantity");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/procurement/contractor-jmrs/${jmr.id}`, { measurement_date: form.measurement_date, measured_qty: form.measured_qty, remarks: form.remarks || null });
      toast.success("JMR updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" title="Enter / edit measurement" data-testid={`edit-jmr-button-${jmr.number}`}><Pencil className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Measurement — {jmr.number}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" value={form.measurement_date} onChange={(e) => setForm({ ...form, measurement_date: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Measured qty</Label><Input required type="number" step="0.001" min="0.001" value={form.measured_qty} onChange={(e) => setForm({ ...form, measured_qty: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
